import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavigationItem } from '../../models/navigation-item.interface';
import { NavigationService } from './navigation.service';

@Component({
  selector: 'tc-navigation',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  templateUrl: './navigation.component.html',
  styleUrl: './navigation.component.css',
})
export class NavigationComponent implements OnInit {
  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  readonly expandedLabels = signal<ReadonlySet<string>>(new Set());
  /** Drives active highlighting under OnPush when route changes outside sidebar clicks. */
  private readonly currentUrl = signal(this.router.url);

  @Input() items?: NavigationItem[];
  @Output() menuItemClicked = new EventEmitter<void>();

  readonly navigationItems = computed(
    () => this.items ?? this.navigationService.items(),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const url =
          event instanceof NavigationEnd
            ? event.urlAfterRedirects
            : this.router.url;
        this.currentUrl.set(url);
        queueMicrotask(() =>
          this.autoExpandActiveParents(this.navigationItems()),
        );
      });
  }

  ngOnInit(): void {
    this.currentUrl.set(this.router.url);
    queueMicrotask(() => this.autoExpandActiveParents(this.navigationItems()));
  }

  hasChildren(item: NavigationItem): boolean {
    return !!item.children?.length;
  }

  isExpanded(item: NavigationItem): boolean {
    return this.expandedLabels().has(item.label) || item.expanded === true;
  }

  toggleExpandItem(item: NavigationItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.hasChildren(item)) {
      return;
    }

    const next = !this.isExpanded(item);
    this.expandedLabels.update((labels) => {
      const updated = new Set(labels);
      if (next) {
        updated.add(item.label);
      } else {
        updated.delete(item.label);
      }
      return updated;
    });

    if (!this.items) {
      this.navigationService.updateItem(item.label, { expanded: next });
    } else {
      item.expanded = next;
    }
  }

  navigateToItem(item: NavigationItem): void {
    if (item.onClick) {
      item.onClick();
      this.menuItemClicked.emit();
      return;
    }
    if (!item.route) {
      return;
    }

    const route = item.route;

    this.zone.run(() => {
      void this.router
        .navigateByUrl(route)
        .finally(() => this.menuItemClicked.emit());
    });
  }

  onExternalLink(): void {
    this.menuItemClicked.emit();
  }

  isActive(item: NavigationItem): boolean {
    if (!item.route) {
      return false;
    }

    const currentUrl = this.currentUrl();
    const exact = currentUrl === item.route;
    const prefix = currentUrl.startsWith(item.route + '/');
    if (!exact && !prefix) {
      return false;
    }

    if (prefix) {
      const mostSpecific = this.getMostSpecificMatchingRoute(currentUrl);
      if (mostSpecific && mostSpecific !== item.route) {
        return false;
      }
    }
    return true;
  }

  hasActiveChild(item: NavigationItem): boolean {
    return !!item.children?.some((child) => this.isActive(child));
  }

  getInnerClasses(item: NavigationItem): Record<string, boolean> {
    const isActive = this.isActive(item);
    const hasActiveChild = this.hasActiveChild(item);
    const highlighted = isActive || hasActiveChild;

    const classes: Record<string, boolean> = {
      ...this.getMenuButtonClasses(highlighted),
      'justify-between': this.hasChildren(item),
    };

    item.classes?.split(' ').forEach((cls) => {
      const trimmed = cls.trim();
      if (trimmed) {
        classes[trimmed] = true;
      }
    });

    return classes;
  }

  getChildClasses(item: NavigationItem): Record<string, boolean> {
    return this.getMenuButtonClasses(this.isActive(item));
  }

  private getMenuButtonClasses(active: boolean): Record<string, boolean> {
    return {
      'cursor-pointer': true,
      flex: true,
      'w-full': true,
      'items-center': true,
      'gap-2': true,
      'overflow-hidden': true,
      'rounded-md': true,
      'p-2': true,
      'text-left': true,
      'text-sm': true,
      'outline-none': true,
      'transition-colors': true,
      'focus-visible:ring-2': true,
      'focus-visible:ring-sidebar-ring': true,
      'bg-transparent': !active,
      'text-sidebar-foreground': !active,
      'bg-sidebar-accent': active,
      'text-sidebar-accent-foreground': active,
      'font-medium': active,
      'hover:bg-sidebar-accent': !active,
      'hover:text-sidebar-accent-foreground': !active,
    };
  }

  private autoExpandActiveParents(items: NavigationItem[]): void {
    const currentUrl = this.currentUrl();
    items.forEach((item) => {
      if (!item.children?.length) {
        return;
      }
      const hasActiveChild = item.children.some(
        (child) => child.route && currentUrl.startsWith(child.route),
      );
      if (hasActiveChild) {
        this.expandedLabels.update((labels) => {
          const next = new Set(labels);
          next.add(item.label);
          return next;
        });
        if (!this.items) {
          this.navigationService.updateItem(item.label, { expanded: true });
        } else {
          item.expanded = true;
        }
      }
    });
  }

  private getMostSpecificMatchingRoute(currentUrl: string): string | null {
    const routes = this.flattenRoutes(this.navigationItems());
    let best: string | null = null;
    for (const route of routes) {
      const matches =
        currentUrl === route || currentUrl.startsWith(route + '/');
      if (matches && (best === null || route.length > best.length)) {
        best = route;
      }
    }
    return best;
  }

  private flattenRoutes(items: NavigationItem[]): string[] {
    const out: string[] = [];
    const walk = (list: NavigationItem[]) => {
      for (const item of list) {
        if (item.route) {
          out.push(item.route);
        }
        if (item.children?.length) {
          walk(item.children);
        }
      }
    };
    walk(items);
    return out;
  }
}
